/**
 * The room relay's protocol — the only part of multiplayer that needs a
 * server, and the smallest one that can exist.
 *
 * The relay never learns what a duel is. It holds a room open for exactly two
 * sockets, tells each which one it is, and copies `relay` frames from one to
 * the other; every SDP and ICE blob is opaque to it. Nothing is persisted, so
 * a restarted dev server forgets every room, which is the correct behaviour
 * for a code that only has to survive the thirty seconds before two peers are
 * talking directly.
 *
 * These types live here rather than in the server so `dev-server.ts` and the
 * browser client are provably talking about the same envelope.
 */

export const SIGNAL_PATH_PREFIX = "/signal/";

/** Which side of the room a socket got. The first to arrive makes the WebRTC offer. */
export type SignalRole = "host" | "guest";

export type SignalMessage =
  /** Relay → client, immediately on connect. */
  | { readonly t: "joined"; readonly role: SignalRole; readonly room: string }
  /** Relay → client: the other seat filled or emptied. */
  | { readonly t: "peer"; readonly present: boolean }
  /** Relay → client: a third socket tried the room, and this is that socket being turned away. */
  | { readonly t: "full" }
  /** Either direction: an opaque blob to hand the other socket verbatim. */
  | { readonly t: "relay"; readonly body: string };

/** The WebRTC handshake, as carried inside a `relay` frame's `body`. */
export type RtcSignal =
  | { readonly t: "offer"; readonly sdp: string }
  | { readonly t: "answer"; readonly sdp: string }
  | { readonly t: "ice"; readonly candidate: string; readonly mid: string | null; readonly index: number | null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function encodeSignal(message: SignalMessage): string {
  return JSON.stringify(message);
}

export function decodeSignal(frame: string): SignalMessage | null {
  let raw: unknown;
  try {
    raw = JSON.parse(frame);
  } catch {
    return null;
  }
  if (!isRecord(raw)) return null;
  if (raw.t === "relay" && typeof raw.body === "string") return { t: "relay", body: raw.body };
  if (raw.t === "peer" && typeof raw.present === "boolean") return { t: "peer", present: raw.present };
  if (raw.t === "full") return { t: "full" };
  if (raw.t === "joined" && (raw.role === "host" || raw.role === "guest") && typeof raw.room === "string") {
    return { t: "joined", role: raw.role, room: raw.room };
  }
  return null;
}

export function encodeRtcSignal(signal: RtcSignal): string {
  return JSON.stringify(signal);
}

export function decodeRtcSignal(body: string): RtcSignal | null {
  let raw: unknown;
  try {
    raw = JSON.parse(body);
  } catch {
    return null;
  }
  if (!isRecord(raw)) return null;
  if ((raw.t === "offer" || raw.t === "answer") && typeof raw.sdp === "string") {
    return raw.t === "offer" ? { t: "offer", sdp: raw.sdp } : { t: "answer", sdp: raw.sdp };
  }
  if (raw.t === "ice" && typeof raw.candidate === "string") {
    const mid = typeof raw.mid === "string" ? raw.mid : null;
    const index = typeof raw.index === "number" ? raw.index : null;
    return { t: "ice", candidate: raw.candidate, mid, index };
  }
  return null;
}

/** The room a signalling request is for, or `null` when the path is not one. */
export function signalRoomOf(pathname: string): string | null {
  if (!pathname.startsWith(SIGNAL_PATH_PREFIX)) return null;
  const room = pathname.slice(SIGNAL_PATH_PREFIX.length);
  return room.length > 0 && !room.includes("/") ? room : null;
}

/**
 * The relay URL for a room, derived from the page's own origin so the same
 * build works on `http://localhost:3000` and behind the user's https reverse
 * proxy without a configured endpoint (`https:` → `wss:`).
 */
export function signalUrl(origin: string, room: string): string {
  const url = new URL(origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${SIGNAL_PATH_PREFIX}${encodeURIComponent(room)}`;
  url.search = "";
  url.hash = "";
  return url.toString();
}
